/**
 * GA4 event tracking (WEB-73 to WEB-76)
 *
 * Sends GA4 events directly via gtag.js (no GTM). gtag.js is already loaded on
 * every page by the existing Google Ads tag in layout/theme.liquid; this file
 * reuses that loader and targets the GA4 property via window.GA4_MEASUREMENT_ID
 * (set from the "GA4 Measurement ID" theme setting). Each event is scoped with
 * send_to so it reaches GA4 only, not the Google Ads account.
 *
 * If no GA4 Measurement ID is configured, all tracking is a no-op.
 *
 * Tracked:
 *   WEB-73 Slider:    view_promotion, select_promotion, slider_navigate
 *   WEB-74 Promotion: view_promotion, select_promotion
 *   WEB-75 Grid:      view_item_list, select_item, select_content
 *   WEB-76 Mobile:    menu_open, menu_navigate, menu_submenu_toggle
 *
 * Markup contract (added in Liquid):
 *   Promotions  -> [data-ga4-promotion] with data-promotion-id / -name /
 *                  data-creative-name / data-creative-slot / data-location-id
 *   Item lists  -> [data-ga4-list] with data-list-id / data-list-name
 *   Product card-> .grid-product[data-product-id] with data-product-title /
 *                  data-product-price
 *   Category    -> .collection-item inside [data-ga4-category]
 */
(function () {
  'use strict';

  var PROMO_CLICK_SELECTOR = [
    '.hero__slide-link',
    '.promo-grid__slide-link',
    'a.promo-grid__container',
    '.promo-grid__container a',
    '.type-banner__link',
    '.countdown__button',
    '.footer-promotion a',
    '.findify-promo-card-cta-button',
    '[data-section-type="background-image"] a'
  ].join(',');

  var observed = typeof WeakSet === 'function' ? new WeakSet() : null;

  // Send a GA4 event via gtag, scoped to the GA4 property only.
  function track(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    var id = window.GA4_MEASUREMENT_ID;
    if (!id) return; // GA4 not configured; do nothing (avoid sending to Ads).
    var data = {};
    if (params) {
      for (var key in params) {
        if (Object.prototype.hasOwnProperty.call(params, key)) {
          data[key] = params[key];
        }
      }
    }
    data.send_to = id;
    window.gtag('event', eventName, data);
  }

  // Resolve the nearest Element for a possibly non-element event target (e.g. SVG/text).
  function closest(node, selector) {
    var el = node;
    while (el && el.nodeType !== 1) {
      el = el.parentNode;
    }
    return el && el.closest ? el.closest(selector) : null;
  }

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function attr(el, name) {
    return el && el.getAttribute ? el.getAttribute(name) || '' : '';
  }

  function promotionData(marker, fallbackEl) {
    var source = marker || fallbackEl;
    var id = attr(marker, 'data-promotion-id');
    var name = attr(marker, 'data-promotion-name');
    var creative = attr(marker, 'data-creative-name');
    var slot = attr(marker, 'data-creative-slot');
    var location = attr(marker, 'data-location-id');

    if (!name && source && source.querySelector) {
      var img = source.querySelector('img[alt]');
      name =
        (img && img.getAttribute('alt')) ||
        text(source.querySelector('.promo-grid__title') || source.querySelector('.h3')) ||
        attr(fallbackEl, 'aria-label');
    }

    var promo = {};
    if (id) promo.promotion_id = id;
    if (name) promo.promotion_name = name;
    if (creative) promo.creative_name = creative;
    if (slot) promo.creative_slot = slot;
    if (location) promo.location_id = location;
    return promo;
  }

  function listContext(el) {
    var listEl = closest(el, '[data-ga4-list]');
    return {
      el: listEl,
      id: attr(listEl, 'data-list-id'),
      name: attr(listEl, 'data-list-name')
    };
  }

  function itemFromCard(card, ctx, index) {
    if (!card) return {};
    var item = { item_id: attr(card, 'data-product-id') };
    var name = attr(card, 'data-product-title');
    if (name) item.item_name = name;
    var price = attr(card, 'data-product-price');
    if (price) {
      var parsed = parseFloat(price);
      if (!isNaN(parsed)) item.price = parsed;
    }
    if (typeof index === 'number' && index >= 0) item.index = index;
    if (ctx && ctx.id) item.item_list_id = ctx.id;
    if (ctx && ctx.name) item.item_list_name = ctx.name;
    return item;
  }

  function indexOfCard(card) {
    if (!card || !card.parentNode) return -1;
    var cards = card.parentNode.querySelectorAll('.grid-product[data-product-id]');
    return Array.prototype.indexOf.call(cards, card);
  }

  function menuTier(el) {
    var levelEl = closest(el, '[data-level]');
    return levelEl ? attr(levelEl, 'data-level') : '';
  }

  function onClick(e) {
    var target = e.target;

    var promoEl = closest(target, PROMO_CLICK_SELECTOR);
    if (promoEl) {
      var marker = closest(promoEl, '[data-ga4-promotion]');
      var promo = promotionData(marker, promoEl);
      var href = attr(promoEl, 'href');
      if (href) promo.link_url = href;
      track('select_promotion', promo);
      return;
    }

    var itemLink = closest(target, '.grid-item__link');
    if (itemLink) {
      var card = closest(itemLink, '.grid-product[data-product-id]') || closest(itemLink, '[data-product-id]');
      var ctx = listContext(itemLink);
      var item = itemFromCard(card, ctx, indexOfCard(card));
      track('select_item', {
        item_list_id: ctx.id,
        item_list_name: ctx.name,
        items: [item]
      });
      return;
    }

    var catLink = closest(target, '.collection-item');
    if (catLink) {
      var catMarker = closest(catLink, '[data-ga4-category]');
      track('select_content', {
        content_type: 'collection',
        item_id: attr(catMarker, 'data-category-id'),
        category_name:
          attr(catMarker, 'data-category-name') || text(catLink.querySelector('.collection-item__title')),
        link_url: attr(catLink, 'href')
      });
      return;
    }

    var subToggle = closest(target, '.js-toggle-submenu');
    if (subToggle && closest(subToggle, '#MobileNav')) {
      track('menu_submenu_toggle', {
        menu_item: text(subToggle.querySelector('.slide-nav__link > span') || subToggle.querySelector('span')),
        menu_tier: menuTier(subToggle)
      });
      return;
    }

    var navLink = closest(target, '#MobileNav a.slide-nav__link');
    if (navLink) {
      track('menu_navigate', {
        menu_item: text(navLink.querySelector('span')),
        link_url: attr(navLink, 'href'),
        menu_tier: menuTier(navLink)
      });
      return;
    }

    var sliderCtrl = closest(target, '.flickity-prev-next-button, .flickity-page-dots .dot');
    if (sliderCtrl && closest(sliderCtrl, '[data-section-type="slideshow-section"]')) {
      var sectionEl = closest(sliderCtrl, '[data-section-id]');
      var navType = sliderCtrl.classList.contains('dot')
        ? 'dot'
        : sliderCtrl.classList.contains('previous')
        ? 'previous'
        : 'next';
      track('slider_navigate', {
        location_id: attr(sectionEl, 'data-section-id'),
        navigation_type: navType
      });
    }
  }

  function fireViewPromotion(el) {
    track('view_promotion', promotionData(el, el));
  }

  function fireViewItemList(listEl) {
    var ctx = { id: attr(listEl, 'data-list-id'), name: attr(listEl, 'data-list-name') };
    var cards = listEl.querySelectorAll('.grid-product[data-product-id]');
    var items = Array.prototype.map.call(cards, function (card, i) {
      return itemFromCard(card, ctx, i);
    });
    if (items.length) {
      track('view_item_list', {
        item_list_id: ctx.id,
        item_list_name: ctx.name,
        items: items
      });
    }
  }

  var promoObserver = null;
  var listObserver = null;

  function setupObservers() {
    if (!('IntersectionObserver' in window)) return;

    promoObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            fireViewPromotion(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    listObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            obs.unobserve(entry.target);
            fireViewItemList(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
  }

  function scanForTargets() {
    if (promoObserver) {
      var promos = document.querySelectorAll('[data-ga4-promotion]');
      Array.prototype.forEach.call(promos, function (el) {
        if (observed && observed.has(el)) return;
        if (observed) observed.add(el);
        promoObserver.observe(el);
      });
    }
    if (listObserver) {
      var lists = document.querySelectorAll('[data-ga4-list]');
      Array.prototype.forEach.call(lists, function (el) {
        if (observed && observed.has(el)) return;
        if (observed) observed.add(el);
        listObserver.observe(el);
      });
    }
  }

  function watchDynamicContent() {
    if (typeof MutationObserver !== 'function') return;
    var pending = null;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        scanForTargets();
      }, 400);
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    // Capture phase so we record the interaction before any navigation occurs.
    document.addEventListener('click', onClick, true);
    document.addEventListener('MobileNav:open', function () {
      track('menu_open', { menu_name: 'mobile_nav' });
    });
    setupObservers();
    scanForTargets();
    watchDynamicContent();
  }

  // Expose helper for ad-hoc events elsewhere if needed.
  window.GA4Events = { track: track };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
